import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Tag, Button, Modal, Form, Input, InputNumber, Select, ColorPicker, message, Switch } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { getAdminTeams, createTeam, updateTeam } from '../../services/adminService';

const { Title } = Typography;

const TeamListPage = () => {
  const [teams, setTeams] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTeam, setEditingTeam] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const data = await getAdminTeams();
      setTeams(data.teams || []);
      setDepots(data.depots || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeams(); }, []);

  const openCreate = () => {
    setCreating(true);
    setEditingTeam(null);
    form.resetFields();
    form.setFieldsValue({ groupe: 'BASE', groupe_type: 'BASE', max_rdv_jour: 3 });
  };

  const handleEdit = (team) => {
    setCreating(false);
    setEditingTeam(team);
    form.setFieldsValue({
      nom: team.nom,
      groupe: team.groupe,
      groupe_type: team.groupe_type,
      depot_id: team.depot_id,
      couleur: team.couleur,
      max_rdv_jour: team.max_rdv_jour,
      deleted: team.deleted === 1,
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const couleur = typeof values.couleur === 'string' ? values.couleur : values.couleur?.toHexString?.() || '#1890ff';
      if (creating) {
        await createTeam({ ...values, couleur });
        message.success('Équipe créée');
        setCreating(false);
      } else {
        await updateTeam(editingTeam.id, {
          ...values,
          couleur,
          deleted: values.deleted ? 1 : 0,
        });
        message.success('Équipe mise à jour');
        setEditingTeam(null);
      }
      fetchTeams();
    } catch (err) {
      if (err.response?.data?.error) {
        message.error(err.response.data.error);
      } else if (!err.errorFields) {
        message.error('Erreur lors de la sauvegarde');
      }
    }
  };

  const columns = [
    {
      title: 'Couleur', dataIndex: 'couleur', width: 50,
      render: (c) => <div style={{ width: 20, height: 20, borderRadius: '50%', background: c }} />,
    },
    { title: 'Nom', dataIndex: 'nom', key: 'nom', sorter: (a, b) => a.nom.localeCompare(b.nom) },
    { title: 'Groupe', dataIndex: 'groupe', key: 'groupe',
      render: (g) => <Tag>{g}</Tag>,
      filters: [...new Set(teams.map(t => t.groupe))].map(g => ({ text: g, value: g })),
      onFilter: (value, record) => record.groupe === value,
    },
    { title: 'Type', dataIndex: 'groupe_type', key: 'groupe_type',
      render: (t) => <Tag color={t === 'BASE' ? 'green' : 'orange'}>{t}</Tag>,
    },
    { title: 'Dépôt', dataIndex: 'depot_ville', key: 'depot_ville' },
    { title: 'Max RDV/jour', dataIndex: 'max_rdv_jour', key: 'max_rdv_jour', width: 110 },
    { title: 'Statut', dataIndex: 'deleted', key: 'deleted',
      render: (d) => d ? <Tag color="red">Inactif</Tag> : <Tag color="green">Actif</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 80,
      render: (_, record) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Modifier</Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Gestion des équipes</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nouvelle équipe
        </Button>
      </div>
      <Card>
        <Table
          dataSource={teams}
          columns={columns}
          loading={loading}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 25 }}
        />
      </Card>

      <Modal
        title={creating ? 'Nouvelle équipe' : "Modifier l'équipe"}
        open={!!editingTeam || creating}
        onCancel={() => { setEditingTeam(null); setCreating(false); }}
        onOk={handleSave}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="nom" label="Nom" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="groupe" label="Groupe">
            <Select options={[
              { value: 'BASE', label: 'BASE' },
              { value: 'SUPPLEMENTAIRE', label: 'SUPPLEMENTAIRE' },
              { value: 'SAV', label: 'SAV' },
            ]} />
          </Form.Item>
          <Form.Item name="groupe_type" label="Type">
            <Select options={[
              { value: 'BASE', label: 'BASE' },
              { value: 'SUPPLEMENTAIRE', label: 'SUPPLEMENTAIRE' },
            ]} />
          </Form.Item>
          <Form.Item name="depot_id" label="Dépôt">
            <Select options={depots.map(d => ({ value: d.id, label: `${d.nom} - ${d.ville}` }))} />
          </Form.Item>
          <Form.Item name="max_rdv_jour" label="Max RDV/jour">
            <InputNumber min={1} max={10} />
          </Form.Item>
          <Form.Item name="couleur" label="Couleur">
            <ColorPicker />
          </Form.Item>
          {!creating && (
            <Form.Item name="deleted" label="Désactivé" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TeamListPage;
